import { useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import type {
  GetAttemptImagesResponseById,
  WorkOrderAttemptListResponse,
  WorkOrderAttemptPatchRequest,
} from '@/types/workOrderAttempt.types';

/** Minimal API error body — avoid deprecated SDK error types. */
export type AttemptServiceErrorBody = {
  message?: string;
  code?: string;
  statusCode?: number;
};

export const useGetAttemptsByWorkOrderId = (workOrderId: string | undefined) => {
  return useQuery<WorkOrderAttemptListResponse, AxiosError<AttemptServiceErrorBody>>({
    queryKey: ['workOrderAttempts', workOrderId],
    queryFn: async () => {
      if (!workOrderId) throw new Error('Work Order ID is required');
      const response = await axios.get<WorkOrderAttemptListResponse>(`/work-orders/${workOrderId}/attempt`);
      return response.data;
    },
    enabled: !!workOrderId,
  });
};

export const getAttemptImagesById = (attemptId: string, workOrderId: string) => {
  return useQuery<GetAttemptImagesResponseById, AxiosError<AttemptServiceErrorBody>>({
    queryKey: ['attempt', attemptId],
    queryFn: async () => {
      if (!attemptId) throw new Error('Attempt ID is required');
      const response = await axios.get<GetAttemptImagesResponseById>(
        `/work-orders/${workOrderId}/attempt/${attemptId}/images`
      );
      return response.data;
    },
    enabled: !!attemptId,
  });
};

export const useSendAttemptMail = () => {
  return useMutation<
    { message: string },
    AxiosError<AttemptServiceErrorBody>,
    { workOrderId: string; imageIds: string[] }
  >({
    mutationFn: async ({ workOrderId, imageIds }) => {
      const response = await axios.post<{ message: string }>(`/work-orders/${workOrderId}/attempt/email`, {
        imageIds,
      });
      return response.data;
    },
  });
};

export const useUpdateWorkOrderAttemptMutation = () => {
  return useMutation<
    { message: string },
    AxiosError<AttemptServiceErrorBody>,
    { workOrderId: string; data: WorkOrderAttemptPatchRequest }
  >({
    mutationFn: async ({ workOrderId, data }) => {
      const response = await axios.patch<{ message: string }>(`/work-orders/${workOrderId}/attempt`, {
        ...data,
      });
      return response.data;
    },
  });
};
